import { Injectable } from '@angular/core'
import { ApiClass } from '@data/class/api-class'
import { IResponse } from '@data/interfaces/iresponseArray.interface'
import { environment } from '@env/environment'
import { catchError, map, Observable, of, retry, throwError, timer } from 'rxjs'

import { AreaPoints, BacklogPage, BoardData, Sprint, WorkItem, WorkItemAttachment, WorkItemComment, WorkItemHistory, WorkItemStatus, WorkUserRef } from '../interfaces/worklog.interface'
import { SprintSummary, WorklogMetrics } from '../interfaces/metrics.interface'
import { Epic, EpicTask, EpicWithStatus, LooseTimelineItem } from '../interfaces/epic.interface'
import { Project, ProjectPayload, ProjectShowcase, ProjectWithStatus } from '../interfaces/project.interface'
import { BugReportPayload, BugReportResult } from '../../../bug-report/data/interfaces/bug-report.interface'
import { BugTraceView } from '../interfaces/bug-trace.interface'

/**
 * Mensaje legible de un error de la API. El backend devuelve el motivo real en `msg`
 * (y lo repite en `data.errors`) — p. ej. «Transición inválida: Backlog → Deploy» o
 * «Solo un miembro del equipo de desarrollo (isDev) puede mover tareas…». Sin esto el
 * usuario solo ve que "no pasó nada".
 */
export function worklogErrorMessage(err: any, fallback = 'No se pudo completar la acción.'): string {
  return err?.error?.msg || err?.error?.data?.errors || err?.message || fallback
}

/** Lo que puede acompañar a un movimiento. Solo viaja lo que se manda: nada de `undefined` en el cuerpo. */
export interface TransitionOptions {
  note?: string
  sprint?: string
  points?: number
  areas?: string[]
  areaPoints?: AreaPoints | null
}

/**
 * Cliente del módulo `worklog` de api-rendafin: Backlog, Tablero, sprints, métricas y catálogo
 * de épicas — todo respaldado por la base. El backend envuelve las respuestas en
 * `{ error, msg, data }` (ResponseInterceptor global) → aquí se desempaqueta `r.data`.
 */
@Injectable({ providedIn: 'root' })
export class WorklogService extends ApiClass {
  private readonly base = environment.apiUrl + 'worklog'

  constructor() {
    super()
  }

  // ─────────── Backlog / Board ───────────

  /**
   * Backlog paginado. `bucket` (ADR-0040 · D8) separa las cubetas: `pool` = sin priorizar ·
   * `next` = comprometida al sprint en planeación. Sin bucket trae ambas.
   *
   * `mine` acota a lo que reportó el usuario autenticado. Va al servidor y no al cliente porque
   * la lista viene acotada a 200: filtrar aquí escondería resultados sin avisar.
   */
  getBacklog(page = 1, limit = 50, filters: Record<string, any> = {}, bucket?: 'pool' | 'next', mine = false): Observable<BacklogPage> {
    const empty: BacklogPage = { data: [], totalItems: 0, page, limit }
    return this.http.post<IResponse>(`${this.base}/backlog`, { page, limit, filters, ...(bucket ? { bucket } : {}), ...(mine ? { mine: true } : {}) }).pipe(
      map((r) => (r?.data as BacklogPage) ?? empty),
      catchError(() => of(empty))
    )
  }

  getBoard(): Observable<BoardData> {
    const empty: BoardData = { sprint: null, columns: {} }
    return this.http.get<IResponse>(`${this.base}/board`).pipe(
      map((r) => (r?.data as BoardData) ?? empty),
      catchError(() => of(empty))
    )
  }

  /** Tareas terminadas (Cerrada), paginado + filtrable. POST /worklog/history. */
  getFinishedTasks(page = 1, limit = 200, filters: Record<string, any> = {}): Observable<BacklogPage> {
    const empty: BacklogPage = { data: [], totalItems: 0, page, limit }
    return this.http.post<IResponse>(`${this.base}/history`, { page, limit, filters }).pipe(
      map((r) => (r?.data as BacklogPage) ?? empty),
      catchError(() => of(empty))
    )
  }

  // ─────────── Items ───────────

  getItem(id: string): Observable<WorkItem | null> {
    return this.http.get<IResponse>(`${this.base}/items/${id}`).pipe(
      map((r) => (r?.data as WorkItem) ?? null),
      catchError(() => of(null))
    )
  }

  /** Tarea por su folio (`F-098`). `null` si no existe: el buscador lo dice, no es un error. */
  getItemByCode(code: string): Observable<WorkItem | null> {
    return this.http.get<IResponse>(`${this.base}/items/by-code/${encodeURIComponent(code)}`).pipe(
      map((r) => (r?.data as WorkItem) ?? null),
      catchError(() => of(null))
    )
  }

  //* Mutaciones: NO llevan `catchError`. El error debe llegar al componente para mostrarle
  //* al usuario el motivo (403 sin isDev, 400 transición inválida). Tragárselo aquí fue la
  //* causa de que mover una tarea "no hiciera nada" sin explicación.
  createItem(dto: Partial<WorkItem>): Observable<WorkItem | null> {
    return this.http.post<IResponse>(`${this.base}/items`, dto).pipe(map((r) => (r?.data as WorkItem) ?? null))
  }

  updateItem(id: string, dto: Partial<WorkItem>): Observable<WorkItem | null> {
    return this.http.patch<IResponse>(`${this.base}/items/${id}`, dto).pipe(map((r) => (r?.data as WorkItem) ?? null))
  }

  /**
   * Mover en el tablero. Requiere isDev (el backend rechaza con 403 si no). Al mandar a To Deploy,
   * `points`, `areas` y el reparto por área viajan en el mismo request: esa etapa exige la tarea ya
   * clasificada.
   */
  transition(id: string, to: WorkItemStatus, opts: TransitionOptions = {}): Observable<WorkItem | null> {
    return this.http
      .patch<IResponse>(`${this.base}/items/${id}/status`, { to, ...opts })
      .pipe(map((r) => (r?.data as WorkItem) ?? null))
  }

  assign(id: string, assignee: string): Observable<WorkItem | null> {
    return this.http.patch<IResponse>(`${this.base}/items/${id}/assign`, { assignee }).pipe(map((r) => (r?.data as WorkItem) ?? null))
  }

  /**
   * Priorización del Backlog (ADR-0040 · D8): mover entre cubetas (`pool` ↔ `next`) y/o fijar
   * posición. Requiere el permiso `prioritize` (Admin / equipo). Sin catchError: el motivo del
   * rechazo (sin sprint planeado, tarea ya en flujo) debe llegar al usuario.
   */
  prioritize(id: string, bucket?: 'pool' | 'next', rank?: number, estimatedPoints?: number): Observable<WorkItem | null> {
    return this.http
      .patch<IResponse>(`${this.base}/items/${id}/prioritize`, {
        ...(bucket ? { bucket } : {}),
        ...(rank != null ? { rank } : {}),
        ...(estimatedPoints != null ? { estimatedPoints } : {}),
      })
      .pipe(map((r) => (r?.data as WorkItem) ?? null))
  }

  /** Resumen de un sprint: comprometido vs entregado, foco, spillover y precisión. */
  getSprintSummary(id: string): Observable<SprintSummary | null> {
    return this.http.get<IResponse>(`${this.base}/sprints/${id}/summary`).pipe(
      map((r) => (r?.data as SprintSummary) ?? null),
      catchError(() => of(null))
    )
  }

  /**
   * Borra la tarea definitivamente. El backend solo lo permite mientras siga en **Backlog**, y solo
   * a quien la registró o a un Admin: fuera de ahí el descarte es `Cancelada`, con motivo y reversa.
   *
   * Sin `catchError`: el motivo del rechazo (403 por ajena, 400 por estado) tiene que llegar al
   * usuario. Tragarlo aquí es lo que hacía que las mutaciones "no hicieran nada".
   */
  deleteItem(id: string): Observable<boolean> {
    return this.http.delete<IResponse>(`${this.base}/items/${id}`).pipe(map(() => true))
  }

  // ─────────── Comentarios / Historial ───────────

  getComments(id: string): Observable<WorkItemComment[]> {
    return this.http.get<IResponse>(`${this.base}/items/${id}/comments`).pipe(
      map((r) => (r?.data as WorkItemComment[]) ?? []),
      catchError(() => of([]))
    )
  }

  addComment(id: string, content: string, mentions: string[] = []): Observable<WorkItemComment | null> {
    return this.http.post<IResponse>(`${this.base}/items/${id}/comments`, { content, mentions }).pipe(map((r) => (r?.data as WorkItemComment) ?? null))
  }

  getHistory(id: string): Observable<WorkItemHistory[]> {
    return this.http.get<IResponse>(`${this.base}/items/${id}/history`).pipe(
      map((r) => (r?.data as WorkItemHistory[]) ?? []),
      catchError(() => of([]))
    )
  }

  // ─────────── Adjuntos ───────────

  getAttachments(id: string): Observable<WorkItemAttachment[]> {
    return this.http.get<IResponse>(`${this.base}/items/${id}/attachments`).pipe(
      map((r) => (r?.data as WorkItemAttachment[]) ?? []),
      catchError(() => of([]))
    )
  }

  /**
   * Sube un archivo a la tarea. Va como `FormData` (multipart) — NO se le pone `Content-Type` a
   * mano: el navegador tiene que agregarlo él para incluir el `boundary`, y fijarlo rompe el
   * parseo en el servidor.
   *
   * `inline` marca lo que se pegó con Cmd/Ctrl+V dentro de un texto: entra a la galería igual,
   * pero no genera su propio evento en Actividad porque el texto que lo contiene ya lo hace.
   */
  uploadAttachment(id: string, file: File, opts: { commentId?: string; inline?: boolean } = {}): Observable<WorkItemAttachment | null> {
    const body = new FormData()
    body.append('file', file, file.name || 'pegada.png')
    if (opts.commentId) body.append('commentId', opts.commentId)
    if (opts.inline) body.append('inline', 'true')
    return this.http.post<IResponse>(`${this.base}/items/${id}/attachments`, body).pipe(map((r) => (r?.data as WorkItemAttachment) ?? null))
  }

  /**
   * Binario de un adjunto, servido por la API.
   *
   * NO se apunta un `<img src>` al blob: el contenedor del bucket no es público y responde 409, así
   * que la imagen nunca pintaría. La API hace de proxy con el token delante — el mismo patrón que
   * `documents/download` y `file-preview.service` —, y el resultado se convierte en object URL.
   */
  getAttachmentBlob(id: string, attachmentId: string): Observable<Blob | null> {
    return this.http.get(`${this.base}/items/${id}/attachments/${attachmentId}/file`, { responseType: 'blob' }).pipe(catchError(() => of(null)))
  }

  removeAttachment(id: string, attachmentId: string): Observable<boolean> {
    return this.http.delete<IResponse>(`${this.base}/items/${id}/attachments/${attachmentId}`).pipe(
      map(() => true),
      catchError(() => of(false))
    )
  }

  /**
   * Reordena varias tareas de un golpe. Es lo que hace posible arrastrar una épica entera: sus
   * tareas se renumeran juntas en un viaje, en vez de una petición por tarea con el orden partido
   * si alguna falla a la mitad.
   *
   * Sin `catchError`, como el resto de las mutaciones: quien arrastra tiene que enterarse del
   * motivo si el servidor lo rechaza.
   */
  reorderItems(items: { id: string; rank: number }[], bucket?: 'pool' | 'next'): Observable<{ updated: number } | null> {
    return this.http
      .patch<IResponse>(`${this.base}/items/reorder`, bucket ? { items, bucket } : { items })
      .pipe(map((r) => (r?.data as { updated: number }) ?? null))
  }

  // ─────────── Reporte rápido de problemas ───────────

  /**
   * Registra un reporte de problema con su bitácora y sus capturas, en UN solo envío.
   *
   * !! Va multipart y no JSON: la API no configura body-parser, así que su límite para JSON es el
   * de Express (100 kb) y la bitácora con una captura lo revienta. Peor aún, ese 413 lo emite
   * Express antes de que Nest lo vea, así que ni siquiera llega envuelto en `{error, msg, data}`.
   *
   * La bitácora viaja como ARCHIVO y no como campo de texto: así esquiva el tope por campo de
   * Multer (1 MB) y no pasa por el validador del cuerpo.
   *
   * Sin `catchError`, como el resto de las mutaciones de este servicio: quien reporta tiene que
   * enterarse del motivo real si no se pudo enviar.
   */
  reportBug(p: BugReportPayload): Observable<BugReportResult | null> {
    const body = new FormData()
    body.append('clientId', p.clientId)
    body.append('title', p.title)
    body.append('description', p.description)
    body.append('route', p.route)
    body.append('screen', p.screen)
    body.append('trace', new Blob([JSON.stringify(p.trace)], { type: 'application/json' }), 'bitacora.json')
    for (const [i, file] of p.shots.entries()) body.append('screenshots', file, file.name || `captura-${i + 1}.jpg`)

    return this.http.post<IResponse>(`${this.base}/bug-reports`, body).pipe(
      map((r) => (r?.data as BugReportResult) ?? null),
      //* Se reintenta solo lo que puede arreglarse solo: sin red o un fallo del servidor. Un 4xx se
      //* reintentaría igual de mal, y un 401 o 403 ya los atendió el ErrorInterceptor.
      retry({ count: 2, delay: (err, n) => (err?.status === 0 || err?.status >= 500 ? timer(n * 1500) : throwError(() => err)) })
    )
  }

  /** La bitácora de un reporte, para pintarla en el detalle de la tarea. */
  getBugTrace(itemId: string): Observable<BugTraceView | null> {
    return this.http.get<IResponse>(`${this.base}/bug-reports/${itemId}`).pipe(
      map((r) => (r?.data as BugTraceView) ?? null),
      catchError(() => of(null))
    )
  }

  /** La bitácora cruda para descargar. Solo responde al equipo de TI. */
  getBugTraceFile(itemId: string): Observable<Blob | null> {
    return this.http.get(`${this.base}/bug-reports/${itemId}/file`, { responseType: 'blob' }).pipe(catchError(() => of(null)))
  }

  // ─────────── Sprints ───────────

  getSprints(): Observable<Sprint[]> {
    return this.http.get<IResponse>(`${this.base}/sprints`).pipe(
      map((r) => (r?.data as Sprint[]) ?? []),
      catchError(() => of([]))
    )
  }

  getCurrentSprint(): Observable<Sprint | null> {
    return this.http.get<IResponse>(`${this.base}/sprints/current`).pipe(
      map((r) => (r?.data as Sprint) ?? null),
      catchError(() => of(null))
    )
  }

  createSprint(dto: Partial<Sprint>): Observable<Sprint | null> {
    return this.http.post<IResponse>(`${this.base}/sprints`, dto).pipe(
      map((r) => (r?.data as Sprint) ?? null),
      catchError(() => of(null))
    )
  }

  updateSprint(id: string, dto: Partial<Sprint>): Observable<Sprint | null> {
    return this.http.patch<IResponse>(`${this.base}/sprints/${id}`, dto).pipe(
      map((r) => (r?.data as Sprint) ?? null),
      catchError(() => of(null))
    )
  }

  // ─────────── Métricas (pestaña Métricas del Roadmap) ───────────

  /**
   * Métricas del equipo ya CALCULADAS por el backend (ADR-0040 · D3): velocidad sobre las
   * fechas reales de cada sprint, calidad, flujo, burn-up y resumen. Antes esto se computaba
   * en el navegador sobre el dataset completo de tareas.
   */
  getMetrics(): Observable<WorklogMetrics | null> {
    return this.http.get<IResponse>(`${this.base}/metrics`).pipe(
      map((r) => (r?.data as WorklogMetrics) ?? null),
      catchError(() => of(null))
    )
  }

  // ─────────── Catálogo de épicas (pestaña Metas y visión) ───────────

  /**
   * Épicas con su estado ya DERIVADO del trabajo (ADR-0040 · D13). Reemplaza a las metas e hitos
   * que se leían de `roadmap.json`: aquel dataset declaraba el estado a mano y divergía del
   * tablero en cuanto una tarea se movía.
   */
  getEpics(): Observable<EpicWithStatus[]> {
    return this.http.get<IResponse>(`${this.base}/catalog/epics`).pipe(
      map((r) => (r?.data as EpicWithStatus[]) ?? []),
      catchError(() => of([]))
    )
  }

  /**
   * El carril suelto de la línea de tiempo: tareas y bugs que no pertenecen a ninguna épica. El
   * backend omite las que no tienen ninguna fecha — en un eje temporal no habría dónde ponerlas.
   */
  getLooseTimeline(): Observable<LooseTimelineItem[]> {
    return this.http.get<IResponse>(`${this.base}/timeline`).pipe(
      map((r) => (r?.data as LooseTimelineItem[]) ?? []),
      catchError(() => of([]))
    )
  }

  //* Las dos mutaciones de épica NO tragan el error, igual que las de proyecto: el motivo real
  //* viene en `msg` —«La épica tiene 9 tarea(s) asociada(s)», «requiere isDev»— y quien llama lo
  //* enseña con `worklogErrorMessage`. Un `catchError` aquí convertiría eso en un botón que no hace
  //* nada.

  /**
   * Corrige una épica. Hoy solo el título: es lo que se equivoca al crearla, y lo demás
   * —estado, fechas, avance— lo deriva el backend del trabajo y no se escribe a mano.
   */
  updateEpic(id: string, dto: { title?: string }): Observable<Epic> {
    return this.http.patch<IResponse>(`${this.base}/catalog/epics/${id}`, dto).pipe(map((r) => r?.data as Epic))
  }

  /**
   * Retira una épica (soft-delete). **El backend se niega si todavía tiene tareas**: borrarla las
   * dejaría sin épica a la que atribuirse en las métricas. La pantalla no debe llegar hasta aquí en
   * ese caso — lo explica antes —, pero la puerta que cuenta es la del servidor.
   */
  removeEpic(id: string): Observable<Epic> {
    return this.http.delete<IResponse>(`${this.base}/catalog/epics/${id}`).pipe(map((r) => r?.data as Epic))
  }

  /** Tareas de una épica, para el desglose desplegable del Gantt. */
  getEpicTasks(slug: string): Observable<EpicTask[]> {
    return this.http.get<IResponse>(`${this.base}/catalog/epics/by-slug/${encodeURIComponent(slug)}/tasks`).pipe(
      map((r) => (r?.data as EpicTask[]) ?? []),
      catchError(() => of([]))
    )
  }

  // ─────────── Catálogo de proyectos (pestaña Proyectos) ───────────

  /**
   * Proyectos con su estado DERIVADO de sus épicas (ADR-0048 · D1).
   *
   * ⚠️ El bloque `commercial` llega SOLO si el usuario es Admin: el backend lo poda antes de
   * responder. Que falte no es que no exista — es que no le toca verlo.
   */
  getProjects(): Observable<ProjectWithStatus[]> {
    return this.http.get<IResponse>(`${this.base}/catalog/projects`).pipe(
      map((r) => (r?.data as ProjectWithStatus[]) ?? []),
      catchError(() => of([]))
    )
  }

  //* Las mutaciones NO tragan el error: el motivo real viene en `msg` y quien llama lo enseña con
  //* `worklogErrorMessage`. Un `catchError` aquí convertiría un 403 en un silencio.
  createProject(dto: ProjectPayload): Observable<Project> {
    return this.http.post<IResponse>(`${this.base}/catalog/projects`, dto).pipe(map((r) => r?.data as Project))
  }

  updateProject(id: string, dto: ProjectPayload): Observable<Project> {
    return this.http.patch<IResponse>(`${this.base}/catalog/projects/${id}`, dto).pipe(map((r) => r?.data as Project))
  }

  /**
   * Cuerpo HTML de la ficha visual. Endpoint aparte del detalle **porque el HTML pesa**: el grid y
   * el modal lo traerían sin usarlo.
   *
   * ⚠️ Solo Admin. El backend responde 403 a cualquier otro rol, y el listado ni siquiera marca
   * `hasShowcase`. Sin `catchError`: quien llama distingue "no hay ficha" (`body: null`) de un
   * fallo real, y tragarse el 403 los volvería la misma cosa.
   */
  getShowcase(slug: string): Observable<ProjectShowcase> {
    return this.http
      .get<IResponse>(`${this.base}/catalog/projects/${encodeURIComponent(slug)}/showcase`)
      .pipe(map((r) => (r?.data as ProjectShowcase) ?? { body: null, updatedAt: null }))
  }

  /** Retira un proyecto (soft-delete). El backend se niega si todavía tiene épicas colgando. */
  removeProject(id: string): Observable<Project> {
    return this.http.delete<IResponse>(`${this.base}/catalog/projects/${id}`).pipe(map((r) => r?.data as Project))
  }

  // ─────────── Usuarios dev (para asignar) ───────────

  /** Miembros del equipo de desarrollo (isDev=true). GET /users/devs. */
  getDevs(): Observable<WorkUserRef[]> {
    return this.http.get<IResponse>(`${environment.apiUrl}users/devs`).pipe(
      map((r) => (r?.data as WorkUserRef[]) ?? []),
      catchError(() => of([]))
    )
  }
}
